// DetailWorkbench — the library's source DETAIL surface, in the approved
// two-pane "reader" layout (library layout 2026-10-01): the file gets the
// detail's FULL width.
//
//   - ONE header: the friendly title with the source's one dominant action
//     ("Create material from this source", F88) beside it, then a quiet facts
//     line (who wrote it, or "Source" · the format · when it was added). Never
//     a chip.
//   - Text tabs: Source · Summary · Notes · Used in materials. A tab exists
//     only when its section has content (`readerTabsOf`); with nothing but the
//     file, no tab row at all.
//   - The Source tab renders the REAL `SourceReading` registry (pdf / image /
//     media / text), keeping its select→annotate gesture live.
//
// UI-AS-PROJECTION. Every durable datum arrives via `source: LoadedSource`;
// the mutations are intents (`onOpenMaterial`, `onDraftFromSource`,
// `onReupload`) the host maps to commands. The active tab is view-ephemeral
// (never survives a reload, carries no domain meaning).

import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { formatCount } from "../../app/displayLimits.js";
import type { LoadedSource } from "../../app/ports/resource.js";
import { Badge } from "../Badge/Badge.js";
import { Button } from "../Button/Button.js";
import { Heading } from "../Heading/Heading.js";
import { Icon } from "../Icon/Icon.js";
import type { MaterialRef, PageSpan } from "../Navigation/types.js";
import { SourceSummary } from "../SourceSummary/SourceSummary.js";
import { SourceReading } from "../SourceView/SourceRenderer.js";
import type { SourceAnnotation } from "../SourceView/SourceView.js";
import { Text } from "../Text/Text.js";
import {
    type ReaderTab,
    readerTabsOf,
    type SourceFact,
    sourceFactsOf,
} from "./detailWorkbenchModel.js";
import "./DetailWorkbench.css";

export type DetailWorkbenchProps = {
    // The author-facing title (the loaded `meta` carries the filename, not this
    // display title — mirrors how `ResourceDetail` fed the old `SourceView`).
    readonly title: string;
    // The whole detail input — the real port shape, no parallel view-model.
    readonly source: LoadedSource;
    // A page span to open the file column at and mark (a read locator).
    readonly focusPages?: PageSpan;
    // Following a derived material opens it — the host's `openRef`.
    readonly onOpenMaterial: (material: MaterialRef) => void;
    // F88 — the source's dominant action, ported from the retired source flik:
    // turn this source into teaching material with the assistant. Absent ⇒ the
    // CTA is not rendered (e.g. a preview surface without the draft intent).
    readonly onDraftFromSource?: () => void;
    // Re-upload the original file into this source when its stored content is
    // missing (shown only in that state). Absent ⇒ no button.
    readonly onReupload?: () => void;
    readonly className?: string;
    // Who wrote the work (e.g. "Bird & de Moor"). Absent ⇒ the facts line
    // names the kind instead. Nothing fills it yet: enrichment folds author +
    // year into one display title; a separate author output is in progress.
    readonly byline?: string;
};

// === Tab bodies ========================================================================================================

// Category badge for one annotation — the placing gesture's own choice
// (comment | highlight), the only real classifier a `SourceAnnotation` carries.
const AnnotationBadge = ({
    category,
}: {
    readonly category: SourceAnnotation["category"];
}): ReactNode =>
    category === "highlight" ? (
        <Badge icon={<Icon name="star" size="sm" />}>
            <Trans>Highlight</Trans>
        </Badge>
    ) : (
        <Badge icon={<Icon name="pencil" size="sm" />}>
            <Trans>Note</Trans>
        </Badge>
    );

const NotesSection = ({
    annotations,
}: {
    readonly annotations: readonly SourceAnnotation[];
}): ReactNode => (
    <section className="dw-meta-section">
        <ul className="dw-notes">
            {annotations.map((note) => (
                <li key={note.id} className="dw-note-card">
                    <div className="dw-note-card__head">
                        <AnnotationBadge category={note.category} />
                    </div>
                    {note.text.length > 0 ? (
                        <p className="dw-note-card__text">{note.text}</p>
                    ) : null}
                </li>
            ))}
        </ul>
    </section>
);

const UsedInSection = ({
    derivedMaterials,
    onOpenMaterial,
}: {
    readonly derivedMaterials: readonly MaterialRef[];
    readonly onOpenMaterial: (material: MaterialRef) => void;
}): ReactNode => (
    <section className="dw-meta-section">
        <ul className="dw-usedin">
            {derivedMaterials.map((material) => (
                <li key={material.id}>
                    <button
                        type="button"
                        className="dw-usedin__row"
                        onClick={() => onOpenMaterial(material)}
                    >
                        <Icon
                            name="material"
                            size="sm"
                            className="dw-usedin__icon"
                        />
                        <span className="dw-usedin__title">
                            {material.title}
                        </span>
                    </button>
                </li>
            ))}
        </ul>
    </section>
);

// === Root ====================================================================

export const DetailWorkbench = ({
    title,
    byline,
    source,
    focusPages,
    onOpenMaterial,
    onDraftFromSource,
    onReupload,
    className,
}: DetailWorkbenchProps): ReactNode => {
    const { t, i18n } = useLingui();
    const [tab, setTab] = useState<ReaderTab>("file");
    const { meta } = source;
    const factText = (fact: SourceFact): string => {
        switch (fact.type) {
            case "byline":
            case "format":
                return fact.text;
            case "kind":
                return t`Source`;
            case "added":
                return fact.date.toLocaleDateString(i18n.locale, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                });
        }
    };
    const tabLabel = (id: ReaderTab): string => {
        switch (id) {
            case "file":
                return t`Source`;
            case "summary":
                return t`Summary`;
            case "notes":
                return t`Notes`;
            case "usedIn":
                return t`Used in materials`;
        }
    };
    const facts = sourceFactsOf({
        byline,
        format: meta.kind,
        addedOnIso: meta.addedOnIso,
    })
        .map(factText)
        .join(" · ");
    const tabs = readerTabsOf(source);
    // A tab whose section emptied (a note deleted elsewhere) falls back to
    // the file rather than stranding an empty body.
    const shown = tabs.some((entry) => entry.id === tab) ? tab : "file";
    const body = (): ReactNode => {
        switch (shown) {
            case "file":
                return (
                    <SourceReading
                        projection={source.content}
                        sourceId={meta.sourceId}
                        annotations={source.annotations}
                        focusPages={focusPages}
                        onReupload={onReupload}
                    />
                );
            case "summary":
                return <SourceSummary summary={source.summary} />;
            case "notes":
                return <NotesSection annotations={source.annotations} />;
            case "usedIn":
                return (
                    <UsedInSection
                        derivedMaterials={source.derivedMaterials}
                        onOpenMaterial={onOpenMaterial}
                    />
                );
        }
    };
    return (
        <div className={`dw-reader${className ? ` ${className}` : ""}`}>
            <header className="dw-reader-head">
                <div className="dw-reader-head__title">
                    <Heading
                        level={1}
                        size="xl"
                        className="bilda-heading--clamp"
                        title={title}
                    >
                        {title}
                    </Heading>
                    {onDraftFromSource !== undefined ? (
                        <Button variant="primary" onClick={onDraftFromSource}>
                            <Trans>Create material from this source</Trans>
                        </Button>
                    ) : null}
                </div>
                <Text size="sm" tone="dim">
                    {facts}
                </Text>
                {tabs.length > 1 ? (
                    <div className="dw-reader-tabs" role="tablist">
                        {tabs.map((entry) => (
                            <button
                                key={entry.id}
                                type="button"
                                role="tab"
                                aria-selected={entry.id === shown}
                                className={`dw-reader-tab${entry.id === shown ? " dw-reader-tab--active" : ""}`}
                                onClick={() => setTab(entry.id)}
                            >
                                {tabLabel(entry.id)}
                                {entry.count !== undefined ? (
                                    <span className="dw-reader-tab__count">
                                        {formatCount(entry.count, i18n.locale)}
                                    </span>
                                ) : null}
                            </button>
                        ))}
                    </div>
                ) : null}
            </header>
            <div
                className={`dw-reader-body${shown === "file" ? "" : " dw-reader-body--prose"}`}
            >
                {body()}
            </div>
        </div>
    );
};
